"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.identifierEntity = exports.identifierSchema = exports.IDENTIFIER_PREFIXES = void 0;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The public identifier beside every uuid key: `<slug>_<eight hex>`.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `proj_a8ed9fc8` (`0144`), `cont_12a3cd5a` (`0152`), and since `0194` every other entity —
 * `client_e9111099`, `wl_0bb16eb3`, `member_21b5a1e2`. The database mints it as a column
 * default, nothing ever edits it, and it is unique across the installation rather than per
 * workspace, because it is the one name meant to be quoted out of context: a URL, a support
 * ticket, an integration's config, the legacy migration's report.
 *
 * ⚠️ **Tasks are the exception by ruling.** A task's identifier is its key, `HSO-142`, derived from
 * the project's `key_prefix` and a counter, because a person types it a hundred times a day.
 *
 * ⚠️ **The slug is the entity's own noun, singular, and this map is the one place it is spelled.**
 * The migration carries the same list; a new entity adds a row to both.
 */
/* ⚠️ Seven are shorter since `0200` (R-57): cont, inv, ot, lr, li, ec, wl. */
exports.IDENTIFIER_PREFIXES = {
    project: 'proj',
    contract: 'cont',
    client: 'client',
    lineItem: 'li',
    invoice: 'inv',
    externalCost: 'ec',
    allocation: 'allocation',
    projectRole: 'project_role',
    member: 'member',
    invitation: 'invitation',
    leaveType: 'leave_type',
    leaveRequest: 'lr',
    leaveQuota: 'leave_quota',
    leaveComment: 'leave_comment',
    overtimeType: 'overtime_type',
    overtimeRequest: 'ot',
    skill: 'skill',
    skillCategory: 'skill_category',
    skillLevel: 'skill_level',
    schedule: 'schedule',
    scheduleAssignment: 'schedule_assignment',
    rateCard: 'rate_card',
    ratePeriod: 'rate_period',
    rateAssignment: 'rate_assignment',
    holiday: 'holiday',
    currency: 'currency',
    metafield: 'metafield',
    taskStatus: 'task_status',
    taskPriority: 'task_priority',
    role: 'role',
    worklog: 'wl',
    taskComment: 'task_comment',
    report: 'report',
    alertException: 'alert_exception',
    alert: 'alert',
    notification: 'notification',
    conversation: 'conversation',
    message: 'message',
    proposal: 'proposal',
    integration: 'integration',
    file: 'file',
    token: 'token',
    export: 'export',
    user: 'user',
    tenant: 'tenant',
    workspace: 'workspace',
    plan: 'plan',
    operator: 'operator',
    /* 20 Oct 2026 · Phase 6 · `0227`–`0228`, minted inline rather than by `0194`'s loop. */
    deal: 'deal',
    contact: 'contact',
    /* 0231 · D9 · a deal's priced alternative. */
    scenario: 'scenario',
    /* 0241 · R-60 · a team the workspace keeps. */
    team: 'team',
};
/** The wire shape of one entity's identifier: the prefix, an underscore, eight lowercase hex. */
const identifierSchema = (entity) => zod_1.z.string().regex(new RegExp(`^${exports.IDENTIFIER_PREFIXES[entity]}_[0-9a-f]{8}$`));
exports.identifierSchema = identifierSchema;
/** Which entity an identifier names, or `null` when it is not one of ours. */
const identifierEntity = (value) => {
    const match = /^([a-z][a-z_]*)_[0-9a-f]{8}$/.exec(value);
    if (match === null)
        return null;
    const prefix = match[1];
    const found = Object.keys(exports.IDENTIFIER_PREFIXES).find((key) => exports.IDENTIFIER_PREFIXES[key] === prefix);
    return found ?? null;
};
exports.identifierEntity = identifierEntity;
//# sourceMappingURL=identifier.js.map